import { useEffect, useState } from "react"

// A viewport shrink smaller than this is browser chrome (the URL bar
// collapsing), not the keyboard — soft keyboards are far taller. Guards against
// reacting to toolbar show/hide.
const KEYBOARD_MIN_PX = 150

/**
 * Height (CSS px) the on-screen keyboard currently covers, or 0 when it's
 * closed.
 *
 * On Android the viewport default `interactive-widget=resizes-visual` overlays
 * the keyboard WITHOUT resizing the layout viewport, so a full-height
 * `100dvh` quiz step can't scroll a focused input (e.g. the email field) out
 * from under it. Feeding this value into the step root's `padding-bottom`
 * shrinks the content box down to the keyboard's top edge, so the flex layout
 * lifts the form above the keyboard (and grows the document into scrollable
 * space when the form is taller than the gap).
 *
 * Derived from `visualViewport`: the layout viewport (`window.innerHeight`)
 * stays full while the visual viewport shrinks by the keyboard height. On
 * browsers that instead resize the layout viewport the delta is ~0, so this is
 * a no-op there (and on desktop). Returns 0 where `visualViewport` is absent.
 */
export function useKeyboardInset(): number {
  const [inset, setInset] = useState(0)

  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return

    const update = () => {
      const covered = window.innerHeight - vv.height
      const next = covered > KEYBOARD_MIN_PX ? Math.round(covered) : 0
      setInset((prev) => (prev !== next ? next : prev))
    }

    update()
    vv.addEventListener("resize", update)
    vv.addEventListener("scroll", update)
    return () => {
      vv.removeEventListener("resize", update)
      vv.removeEventListener("scroll", update)
    }
  }, [])

  return inset
}
